export interface ParsedCronExpression {
  cron: string;
  timezone?: string;
}

export type DiscoverySiteType = 'onPrem' | 'awsCloud' | 'azureCloud';

export const normalizeDiscoverySiteType = (siteType?: string | null): DiscoverySiteType => {
  switch ((siteType ?? '').trim().toLowerCase()) {
    case 'awscloud':
      return 'awsCloud';
    case 'azurecloud':
      return 'azureCloud';
    default:
      return 'onPrem';
  }
};

export const parseCronWithTimezone = (value?: string | null): ParsedCronExpression => {
  if (!value) {
    return { cron: '' };
  }

  const trimmed = value.trim();
  const match = trimmed.match(/^CRON_TZ=([^\s]+)\s+(.+)$/i);
  if (match) {
    return {
      timezone: match[1],
      cron: match[2]
    };
  }

  return { cron: trimmed };
};

export const buildCronWithTimezone = (cron: string, timezone?: string): string => {
  if (!cron.trim()) {
    return cron.trim();
  }
  return timezone ? `CRON_TZ=${timezone} ${cron.trim()}` : cron.trim();
};

export const listFromInput = (value: string): string[] => {
  return value
    .split(/[\n,]+/g)
    .map((item) => item.trim())
    .filter(Boolean);
};

export const describeSchedule = (cronRaw?: string | null): string => {
  if (!cronRaw) {
    return 'Manual';
  }
  const parsed = parseCronWithTimezone(cronRaw);
  const tz = parsed.timezone ? ` (${parsed.timezone})` : '';
  const parts = parsed.cron.trim().split(/\s+/);
  if (parts.length === 5) {
    const [minute, hour, , , dow] = parts;
    const minuteNum = minute === '*' ? 0 : Number(minute);
    const hourNum = hour === '*' ? 0 : Number(hour);
    const formatTime = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    if (hour.startsWith('*/')) {
      const m = isNaN(minuteNum) ? 0 : minuteNum;
      return `Hourly at :${String(m).padStart(2, '0')}${tz}`;
    }
    if (dow === '*' || dow === '?') {
      if (!isNaN(hourNum) && !isNaN(minuteNum)) {
        return `Daily at ${formatTime(hourNum, minuteNum)}${tz}`;
      }
    } else if (!isNaN(hourNum) && !isNaN(minuteNum)) {
      const dayMap: Record<string, string> = {
        '0': 'Sunday',
        '1': 'Monday',
        '2': 'Tuesday',
        '3': 'Wednesday',
        '4': 'Thursday',
        '5': 'Friday',
        '6': 'Saturday'
      };
      const day = dayMap[dow] ?? `Day ${dow}`;
      return `${day}s at ${formatTime(hourNum, minuteNum)}${tz}`;
    }
  }
  return `${parsed.cron}${tz}`;
};

export const describeDiscoverySchedule = (
  schedule?: { mode?: string | null; cron?: string | null; triggerScope?: string | null } | null
): string => {
  const mode = (schedule?.mode ?? '').trim().toLowerCase();
  if (mode === 'manual') {
    return 'Manual';
  }
  return describeSchedule(schedule?.cron);
};

export const formatDiscoveryTriggerScope = (triggerScope?: string | null): string => {
  const trimmed = (triggerScope ?? '').trim();
  if (!trimmed) {
    return '';
  }
  return trimmed
    .split('_')
    .filter(Boolean)
    .map((part) => {
      const lower = part.toLowerCase();
      return part.length <= 3 ? part.toUpperCase() : `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
    })
    .join(' ');
};
